import React, { useState, useEffect } from 'react';
import type { ListingClientType, ListingClientDocumentType, MaritalStatus } from '../../../types/ListingTypes';
import { useListingContext } from '../../../contexts/ListingContext';
import styles from '../../../styles/ListingManagement.module.css';
import buttonStyles from '../../../styles/Buttons.module.css';

// ─── Shared staging types (exported for ClientList + AddListing) ──────────────

export interface PendingDoc {
  file: File;
  documentTypeId: string;
  documentTypeName: string;
}

export interface StagedClient {
  tempId: string;
  clientTypeId: string;
  clientTypeName: string;
  name: string;
  surname: string;
  email: string;
  cell: string;
  streetNumber: string;
  streetName: string;
  city: string;
  province: string;
  postalCode: string;
  maritalStatus: MaritalStatus;
  documents: PendingDoc[];
}

// ─── Component ────────────────────────────────────────────────────────────────

interface AddClientProps {
  clientTypes: ListingClientType[];
  onAdd: (client: StagedClient) => void;
  onCancel: () => void;
}

const MARITAL_STATUSES: MaritalStatus[] = [
  'Married', 'Single', 'Divorced', 'Separated', 'Widowed',
];

const PROVINCES = [
  'Eastern Cape', 'Free State', 'Gauteng', 'KwaZulu-Natal',
  'Limpopo', 'Mpumalanga', 'North West', 'Northern Cape', 'Western Cape',
];

interface FormState {
  clientTypeId: string;
  name: string;
  surname: string;
  email: string;
  cell: string;
  streetNumber: string;
  streetName: string;
  city: string;
  province: string;
  postalCode: string;
  maritalStatus: MaritalStatus | '';
}

const EMPTY_FORM: FormState = {
  clientTypeId: '',
  name: '',
  surname: '',
  email: '',
  cell: '',
  streetNumber: '',
  streetName: '',
  city: '',
  province: '',
  postalCode: '',
  maritalStatus: '',
};

const AddClient: React.FC<AddClientProps> = ({ clientTypes, onAdd, onCancel }) => {
  const { fetchClientDocumentTypes } = useListingContext();
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [clientDocumentTypes, setClientDocumentTypes] = useState<ListingClientDocumentType[]>([]);
  const [docs, setDocs] = useState<Record<string, File>>({});
  const [error, setError] = useState<string | null>(null);

  // Fetch document types whenever the selected client type changes
  useEffect(() => {
    if (!form.clientTypeId) {
      setClientDocumentTypes([]);
      setDocs({});
      return;
    }
    fetchClientDocumentTypes(form.clientTypeId)
      .then(setClientDocumentTypes)
      .catch(() => {});
    setDocs({});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.clientTypeId]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (documentTypeId: string, file: File | null) => {
    setDocs(prev => {
      if (!file) {
        const { [documentTypeId]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [documentTypeId]: file };
    });
  };

  const handleSubmit = () => {
    setError(null);
    if (!form.clientTypeId) { setError('Please select a client type.'); return; }
    if (!form.name.trim()) { setError('Name is required.'); return; }
    if (!form.surname.trim()) { setError('Surname is required.'); return; }
    if (!form.email.trim()) { setError('Email is required.'); return; }
    if (!form.cell.trim()) { setError('Cell number is required.'); return; }
    if (!form.maritalStatus) { setError('Marital status is required.'); return; }
    if (!form.streetNumber || isNaN(Number(form.streetNumber))) { setError('Street number must be a valid number.'); return; }
    if (!form.streetName.trim()) { setError('Street name is required.'); return; }
    if (!form.city.trim()) { setError('City is required.'); return; }
    if (!form.province) { setError('Province is required.'); return; }
    if (!form.postalCode.trim()) { setError('Postal code is required.'); return; }

    const clientType = clientTypes.find(ct => ct.id === form.clientTypeId)!;
    const documents: PendingDoc[] = Object.entries(docs).map(([docTypeId, file]) => {
      const docType = clientDocumentTypes.find(dt => dt.id === docTypeId);
      return { file, documentTypeId: docTypeId, documentTypeName: docType?.name ?? '' };
    });

    onAdd({
      tempId: `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      clientTypeId: form.clientTypeId,
      clientTypeName: clientType.name,
      name: form.name.trim(),
      surname: form.surname.trim(),
      email: form.email.trim(),
      cell: form.cell.trim(),
      streetNumber: form.streetNumber,
      streetName: form.streetName.trim(),
      city: form.city.trim(),
      province: form.province,
      postalCode: form.postalCode.trim(),
      maritalStatus: form.maritalStatus as MaritalStatus,
      documents,
    });
  };

  const textField = (
    label: string,
    name: keyof FormState,
    type: 'text' | 'number' | 'email' | 'tel' = 'text',
  ) => (
    <div className={styles.categoryRow}>
      <label className={styles.categoryLabel}>
        {label} <span style={{ color: '#dc3545' }}>*</span>
      </label>
      <div className={styles.categoryInputWrapper}>
        <input
          className={styles.categoryInput}
          type={type}
          name={name}
          value={form[name] as string}
          onChange={handleChange}
        />
      </div>
    </div>
  );

  const selectField = (
    label: string,
    name: keyof FormState,
    options: string[],
  ) => (
    <div className={styles.categoryRow}>
      <label className={styles.categoryLabel}>
        {label} <span style={{ color: '#dc3545' }}>*</span>
      </label>
      <div className={styles.categoryInputWrapper}>
        <select
          className={styles.categoryInput}
          name={name}
          value={form[name] as string}
          onChange={handleChange}
        >
          <option value="">— Select —</option>
          {options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
    </div>
  );

  return (
    <div className={styles.clientFormContainer}>
      <h4 className={styles.clientFormHeader}>Add Client</h4>

      {/* Client Type */}
      <div className={styles.categoryRow}>
        <label className={styles.categoryLabel}>
          Client Type <span style={{ color: '#dc3545' }}>*</span>
        </label>
        <div className={styles.clientTypeSelectWrapper}>
          <select
            className={styles.clientTypeSelect}
            name="clientTypeId"
            value={form.clientTypeId}
            onChange={handleChange}
          >
            <option value="">— Select —</option>
            {clientTypes.map(ct => (
              <option key={ct.id} value={ct.id}>{ct.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Details */}
      <div className={styles.clientFormTwoColumn}>
        <div className={styles.clientFormColumn}>
          <p className={styles.clientFormColumnTitle}>Personal Details</p>
          {textField('Name', 'name')}
          {textField('Surname', 'surname')}
          {textField('Email', 'email', 'email')}
          {textField('Cell', 'cell', 'tel')}
          {selectField('Marital Status', 'maritalStatus', MARITAL_STATUSES)}
        </div>

        <div className={styles.clientFormColumn}>
          <p className={styles.clientFormColumnTitle}>Address</p>
          {textField('Street Number', 'streetNumber', 'number')}
          {textField('Street Name', 'streetName')}
          {textField('City', 'city')}
          {selectField('Province', 'province', PROVINCES)}
          {textField('Postal Code', 'postalCode')}
        </div>
      </div>

      {/* Documents */}
      {clientDocumentTypes.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <p className={styles.clientFormColumnTitle}>Documents</p>
          {clientDocumentTypes.map(dt => {
            const file = docs[dt.id];
            return (
              <div key={dt.id} className={file ? styles.docCardWithDoc : styles.docCard}>
                <div className={styles.docCardRow}>
                  <div className={styles.docCardInfo}>
                    <span className={styles.documentLabel}>{dt.name}</span>
                    {file ? (
                      <div className={styles.docCardFileInfo}>
                        <span className={styles.docCardFileName}>{file.name}</span>
                        <span className={styles.docCardFileSize}>{(file.size / 1024).toFixed(1)} KB</span>
                        <span className={styles.docCardReady}>Ready to upload</span>
                      </div>
                    ) : (
                      <div className={styles.docUploadRow}>
                        <input
                          type="file"
                          onChange={e => handleFileChange(dt.id, e.target.files?.[0] ?? null)}
                        />
                      </div>
                    )}
                  </div>
                  {file && (
                    <button
                      type="button"
                      className={`${buttonStyles.button} ${buttonStyles.docRemove}`}
                      onClick={() => handleFileChange(dt.id, null)}
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {error && <p className={styles.agreementFormError}>{error}</p>}

      <div className={styles.modalFormActionsEditMode}>
        <button
          type="button"
          className={`${buttonStyles.button} ${buttonStyles.primary}`}
          onClick={handleSubmit}
        >
          Add Client
        </button>
        <button
          type="button"
          className={`${buttonStyles.button} ${buttonStyles.secondary}`}
          onClick={onCancel}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default AddClient;
